"use client";

import { Component, type ReactNode } from "react";
import * as Sentry from "@sentry/nextjs";

/**
 * One error boundary per page block, so a single bad block costs its own section
 * instead of the whole page.
 *
 * WHY this exists: page documents are opaque JSON authored by customers and
 * round-tripped through backend storage, and twice in one day a single malformed
 * field took down an entire marketing page — a locale map the resolver didn't
 * recognise reached React as a child (minified #31), and because the body sat
 * under ONE boundary the visitor got "Something went wrong" instead of a
 * catalogue. The blast radius, not the parsing, was the real defect.
 *
 * PAIRED WITH SUSPENSE, and that pairing is load-bearing — measured, not assumed.
 * A class boundary alone catches CLIENT render errors but cannot contain a throw
 * during SERVER rendering: React has no way to fall back to a client boundary
 * mid-stream without a Suspense boundary to defer to, so it abandons the whole
 * subtree. Verified by poisoning one block: with the boundary alone the page came
 * back with an EMPTY body (150 KB, every section gone); with Suspense around each
 * boundary only the poisoned block vanished (230 KB, all siblings intact).
 *
 * `PageRenderer` avoids Puck's `<Render>` because ITS Suspense wrapper pushed the
 * hero into an out-of-order streaming placeholder and moved the hero's
 * `<link rel="preload">` out of `<head>`. That does not apply here: Puck wrapped
 * the whole document in ONE boundary, while these are per block and a boundary
 * that never suspends emits its HTML inline. Measured against the pre-change
 * build: hero position and preload count identical, +2 streaming placeholders
 * (the two async module grids) and +1.7 KB. Re-measure before removing this.
 *
 * A failed block renders NOTHING for visitors: a marketing page missing one
 * section still reads as intentional, whereas an inline error panel does not.
 * It is never silent to us, though — every failure is reported to Sentry with
 * the block type and id, and surfaced in the console outside production.
 */
type Props = { blockType: string; blockId: string; children: ReactNode };
type State = { failed: boolean };

export class BlockBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    // Errors caught by a boundary never reach window.onerror, so without this
    // the page would degrade gracefully and we would never hear about it —
    // which is worse than the crash it replaces (same reasoning as RouteError).
    Sentry.captureException(error, {
      tags: { area: "page-block", block: this.props.blockType },
      extra: { blockId: this.props.blockId },
    });
    if (process.env.NODE_ENV !== "production") {
      console.error(
        `[page-block] "${this.props.blockType}" (${this.props.blockId}) failed to render`,
        error,
      );
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    // Visible only outside production: the author/developer sees WHICH block
    // broke, visitors just see the section absent.
    if (process.env.NODE_ENV === "production") return null;
    return (
      <div className="border border-red-300/60 bg-red-50/60 p-4 text-[12.5px] text-red-900 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
        <p className="font-display font-bold uppercase tracking-[0.18em] text-[10.5px] text-red-700 dark:text-red-300">
          Block failed to render — dev only
        </p>
        <p className="mt-1 font-mono">
          {this.props.blockType} · {this.props.blockId}
        </p>
      </div>
    );
  }
}
